import { Logger, LoggerService } from "@Shared/logger";
import { UrlUtil } from "@Shared/utils";
import { AUTH0_CONST, Auth0Service } from "@Modules/auth_module";
import { APPLE_CONST } from "../_models";
import { AppleRepository } from "../repository/apple.repository";

export class AppleService {
  private Logger: Logger = LoggerService.createLogger("AppleService");
  private static instance: AppleService;
  static get Instance(): AppleService {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new AppleService();
    }
    return this.instance;
  }

  private appleRepo: AppleRepository;
  get AppleRepo(): AppleRepository {
    return this.appleRepo;
  }

  constructor() {
    this.Logger.trace("****  CTOR ****");
    this.appleRepo = AppleRepository.Instance;
  }

  /**
   * Pre-Auth0 flow: user landed on /callback?redirect_uri=<...applenews...> straight from
   * Apple News, before Auth0 has been involved. We need to redirect into Auth0 login and
   * tag the final destination so we know to run the Apple sub-check on the way back.
   */
  get shouldHandlePreAppleLogin(): boolean {
    if (!window.location.pathname.startsWith('/callback')) return false;
    const redirectUri = UrlUtil.getQueryParam(APPLE_CONST.redirectUriParam);
    return !!redirectUri && redirectUri.includes(APPLE_CONST.appleFlowIdentifier);
  }

  /**
   * Post-Auth0 flow: user returned to /callback after Auth0 with auth_redirect carrying
   * the post-apple-login marker. The Apple sub-check + token append must run before the
   * final window.location.assign.
   */
  get isPostAppleCallback(): boolean {
    const authRedirect = UrlUtil.getQueryParam(AUTH0_CONST.authRedirectParam) || '';
    return authRedirect.includes(APPLE_CONST.postAppleLoginParam);
  }

  /**
   * Tags the Apple News destination URL with `post-apple-login=true` and kicks off the
   * standard Auth0 redirect login so the marker survives the round-trip back to /callback.
   */
  public async initiatePreAppleLogin(): Promise<void> {
    const redirectUriString = UrlUtil.getQueryParam(APPLE_CONST.redirectUriParam);
    if (!redirectUriString) {
      this.Logger.warn("initiatePreAppleLogin called without a redirect_uri param");
      return;
    }
    try {
      const destination = new URL(redirectUriString);
      destination.searchParams.set(APPLE_CONST.postAppleLoginParam, 'true');
      this.Logger.trace("Pre-Apple destination after tagging", destination.toString());

      const auth0Service = Auth0Service.Instance;
      const callbackUrl = new URL(auth0Service.Auth0Options.callbackURL);
      callbackUrl.searchParams.set(AUTH0_CONST.authRedirectParam, destination.toString());

      await auth0Service.AuthClient.loginWithRedirect({
        authorizationParams: {
          redirect_uri: callbackUrl.toString()
        }
      });
    } catch (error) {
      this.Logger.error("Failed to initiate pre-Apple login", error);
    }
  }

  /**
   * After Auth0 callback completes: resolves the encrypted uuid for the current Auth0 user
   * and checks Apple subscription status. Returns the encrypted uuid only when the user is
   * subscribed (to be appended to the Apple News redirect URL as access_token); otherwise null.
   */
  public async resolveAppleAccessToken(): Promise<string | null> {
    const user = await Auth0Service.Instance.getUser();
    if (!user?.sub) {
      this.Logger.warn("resolveAppleAccessToken: no Auth0 user available");
      return null;
    }

    const encryptedId = await this.appleRepo.getEncryptedUuid(user.sub);
    if (!encryptedId) {
      this.Logger.warn("resolveAppleAccessToken: no encryptedUuid returned");
      return null;
    }
    this.Logger.trace("Encrypted apple uuid resolved");

    const subscribed = await this.appleRepo.checkSubStatus(encryptedId);
    this.Logger.trace("Apple sub status", subscribed);

    return subscribed ? encryptedId : null;
  }

  /**
   * Strips the temporary post-apple-login marker from the destination URL and, if the user
   * is a verified Apple subscriber, appends their encrypted uuid as the access_token query
   * param so the Apple News reader can authenticate the entitlement.
   */
  public buildPostAppleRedirectUrl(authRedirect: string, appleAccessToken: string | null): URL {
    const decoded = new URL(decodeURIComponent(authRedirect));
    decoded.searchParams.delete(APPLE_CONST.postAppleLoginParam);
    if (appleAccessToken) {
      decoded.searchParams.set(APPLE_CONST.accessTokenParam, appleAccessToken);
    }
    return decoded;
  }
}
